import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DeleteApprovalModal = ({ isOpen, onClose, onConfirm, properties, userRole }) => {
  const [adminPassword, setAdminPassword] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.9,
      y: 30
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1],
        scale: {
          type: 'spring',
          damping: 20,
          stiffness: 300
        }
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.9,
      y: 30,
      transition: { 
        duration: 0.15,
        ease: 'easeIn'
      }
    }
  };

  const shakeVariants = {
    shake: {
      x: [0, -10, 10, -10, 10, 0],
      transition: { duration: 0.4 }
    }
  };

  const handleConfirm = () => {
    // Frontend-only validation
    if (userRole !== 'admin') {
      setError('فقط المسؤول يمكنه حذف العقارات');
      return;
    }

    if (!adminPassword) {
      setError('يرجى إدخال كلمة المرور للتأكيد');
      return;
    }

    // Simple frontend validation (in real app, this would be backend)
    if (adminPassword !== 'admin123') {
      setError('كلمة المرور غير صحيحة');
      return;
    }

    if (!reason?.trim()) {
      setError('يرجى إدخال سبب الحذف');
      return;
    }

    // Call the confirm callback with deletion details
    onConfirm({
      propertyIds: properties,
      reason: reason,
      timestamp: new Date()?.toISOString(),
      approvedBy: 'admin'
    });

    // Reset and close
    setAdminPassword('');
    setReason('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    setAdminPassword('');
    setReason('');
    setError('');
    onClose();
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div 
            className="relative bg-card rounded-lg border border-border shadow-warm w-full max-w-md"
            variants={modalVariants}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/30">
              <h3 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} color="#ef4444" />
                تأكيد حذف العقار
              </h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                iconSize={20}
                onClick={handleClose}
              />
            </div>

            <motion.div 
              className="p-4 space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.2 }}
            >
              <motion.div 
                className="bg-error/10 border border-error/20 rounded-lg p-3"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
              >
                <p className="text-sm text-error font-medium">
                  ⚠️ تحذير: هذا الإجراء لا يمكن التراجع عنه
                </p>
                <p className="text-xs text-error/80 mt-1">
                  سيتم حذف {properties?.length} عقار بشكل نهائي
                </p>
              </motion.div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  كلمة مرور المسؤول *
                </label>
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e?.target?.value)}
                  placeholder="أدخل كلمة المرور للتأكيد"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  سبب الحذف *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e?.target?.value)}
                  placeholder="اذكر سبب حذف هذا العقار..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {error && (
                <motion.div 
                  className="bg-error/10 border border-error/20 rounded-lg p-3"
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1 }}
                  variants={shakeVariants}
                  animate="shake"
                >
                  <p className="text-sm text-error">{error}</p>
                </motion.div>
              )}

              <motion.div 
                className="flex gap-2 pt-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.2 }}
              >
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleClose}
                >
                  إلغاء
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleConfirm}
                  className="bg-error hover:bg-error/90"
                >
                  تأكيد الحذف
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteApprovalModal;